import React, {FC, useContext, useEffect, useState} from 'react';
import cn from 'classnames';
import {useLocalizer} from '@/common/services/localizer';
import {useInputState} from '@/common/helpers/hooks/useInputState';
import {getGeolocationIfGranted} from '@/common/helpers/geolocation/geolocationHelper';
import {ServicesContext} from '@/feedback/contexts/ServicesContext';
import styles from './Form.css';

const validEmailRegexp = /^[-a-z0-9'_%+~#!$&*=]+(\.[-a-z0-9'_%+~#!$&*=]+)*@([a-z0-9][-a-z0-9]*\.)+[a-z]{2,20}$/i;

interface FormProps {
	mode: 'general' | 'ads';
	skin: string;
	onSent(): void;
}

export const Form: FC<FormProps> = props => {
	const {mode, skin, onSent} = props;

	const t = useLocalizer();
	const {feedbackAPI} = useContext(ServicesContext)!;

	const [emailValue, setEmailValue] = useInputState('');
	const [textValue, setTextValue] = useInputState('');

	const [submitting, setSubmitting] = useState(false);

	const [emailError, setEmailError] = useState<string | null>(null);
	const [textError, setTextError] = useState<string | null>(null);

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const cleanEmail = emailValue.trim();
		if (!validEmailRegexp.test(cleanEmail) || cleanEmail.split('@')[0].length > 64) {
			setEmailError('invalid');
			return;
		}
		if (textValue.trim().length === 0) {
			setTextError('invalid');
			return;
		}

		setSubmitting(true);

		let geoLocation;
		try {
			geoLocation = await getGeolocationIfGranted({timeout: 8000});
		} catch (error) {
			console.error(error);
		}

		try {
			await feedbackAPI.sendFeedback(mode, cleanEmail, textValue, {
				name: skin,
				version: APP_VERSION,
				screen: {
					width: window.screen.width,
					height: window.screen.height,
				},
				geoLocation,
			});
		} finally {
			setSubmitting(false);
		}

		onSent();
	};

	// Reset errors on a value changes
	useEffect(() => {
		if (emailError) {
			setEmailError(null);
		}
	}, [emailValue]);

	useEffect(() => {
		if (textError) {
			setTextError(null);
		}
	}, [textValue]);

	return (
		<form className={styles.root} onSubmit={handleSubmit}>
			<label>
				<p className={styles.label}>{t('emailTitle')}</p>
				<input
					className={cn(styles.field, emailError && styles.isFailed)}
					type='text'
					name='email'
					inputMode='email'
					placeholder='E-mail'
					autoFocus
					maxLength={500}
					value={emailValue}
					onChange={setEmailValue}
				/>
			</label>
			{emailError && <p className={styles.error}>{t(`emailError.${emailError}`)}</p>}

			<label>
				<p className={styles.label}>{t('textTitle')}</p>
				<textarea
					className={cn(styles.textarea, textError && styles.isFailed)}
					rows={8}
					maxLength={1000}
					placeholder={t(`textPlaceholder.${mode}`)}
					value={textValue}
					onChange={setTextValue}
				/>{' '}
			</label>

			{textError ? (
				<p className={styles.error}>{t(`textError.${textError}`)}</p>
			) : (
				<p className={styles.textareaCounter}>{textValue.length} / 1000</p>
			)}

			<button className={styles.submit} type='submit' disabled={submitting || !emailValue || !textValue}>
				{t('send')}
			</button>
		</form>
	);
};

Form.displayName = 'Form';
